/**
 * RouteMap — the old viewer's map primitive. An adapter: it maps the
 * destination providers (cameras, event, selection, tracking) and the shell
 * surface onto {@link CourseMap}, which owns the map, the camera spheres,
 * their interaction and the map performance contract.
 *
 *   - Watching a camera selects it and asks the shell to flip to camera mode
 *     via {@link useShellSurface}: the shell's camera canvas is the player.
 *   - The map follows the selection once the viewer has picked a camera, or
 *     when there is no course to frame.
 *   - The producer's featured camera carries the sphere's star.
 *   - Tracked entities from the configured source ride along as markers.
 */

import { useCallback, useMemo, type ReactNode } from 'react'
import { Marker } from 'react-map-gl/mapbox-legacy'
import { CourseMap, type CourseMapBasemap, type CourseMapCamera } from '@/components/map/course-map'
import {
  registerPrimitive,
  useShellSurface,
  type PrimitiveProps,
} from '../../layout'
import {
  CameraVideo,
  useCameraSelection,
  useCameras,
  useEvent,
  useTrackedEntities,
} from '../../destination'

interface RouteMapConfig {
  sourceKey?: string
  basemap?: CourseMapBasemap
}

/** Live video masked into the sphere. Falls back to the preview image
 *  while the LiveKit track is still subscribing, then to the initials. */
function renderBubbleVideo(camera: CourseMapCamera, fallback: ReactNode) {
  return (
    <CameraVideo
      identity={camera.identity}
      className="lv-sphere-video"
      previewClassName="lv-sphere-video"
      alt=""
      fallback={fallback}
    />
  )
}

export function RouteMap({ config }: PrimitiveProps) {
  const cameras = useCameras()
  const event = useEvent()
  const selection = useCameraSelection()
  const { mode: surfaceMode, requestMode } = useShellSurface()
  const cfg = config as RouteMapConfig
  const trackedEntities = useTrackedEntities(cfg.sourceKey)

  // Memoised so the map's follow-selection ease runs only when cameras change.
  const mapCameras = useMemo<CourseMapCamera[]>(
    () => cameras.map(({ identity, name, coordinate, online }) => ({ identity, name, coordinate, online })),
    [cameras],
  )

  const route = useMemo(
    () => (event.route
      ? { id: event.route.id, gpx_url: event.route.gpxUrl, bounds: event.route.bounds }
      : null),
    [event.route],
  )

  // If the host shell has no surface concept (mode === null) we still select
  // but skip the request.
  const watch = useCallback(
    (identity: string) => {
      selection.select(identity)
      if (surfaceMode !== null) requestMode('camera')
    },
    [selection, surfaceMode, requestMode],
  )

  return (
    <CourseMap
      eventId={event.id}
      route={route}
      cameras={mapCameras}
      selectedIdentity={selection.selectedIdentity}
      calledIdentity={selection.producerFeaturedIdentity}
      followSelection={selection.hasViewerOverride}
      onWatch={watch}
      renderVideo={renderBubbleVideo}
      basemap={cfg.basemap}
    >
      {trackedEntities.filter((entity) => entity.coordinate).map((entity) => (
        <Marker
          key={`tracked-${entity.id}`}
          longitude={entity.coordinate!.longitude}
          latitude={entity.coordinate!.latitude}
          anchor="center"
        >
          <span
            className="lv-tracked-entity"
            role="img"
            aria-label={`${entity.name}, live tracked position`}
            title={entity.name}
          >
            <span className="lv-tracked-entity-dot" />
            <span className="lv-tracked-entity-label">{entity.name}</span>
          </span>
        </Marker>
      ))}
    </CourseMap>
  )
}

registerPrimitive('RouteMap', RouteMap)
